import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, Loader2, TrendingDown, Dumbbell, Apple, Video as VideoIcon,
  Bell, Calendar, DollarSign, Heart, Trash2, ExternalLink,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import { MemberPackagesAdmin } from "../components/booking/MemberPackagesAdmin";

const CAT_LABELS = {
  jambes_fessiers: "Jambes / Fessiers",
  haut_corps: "Haut du corps",
  cardio: "Cardio / Abdos",
  abdos_dos: "Abdos / Bas du dos",
};

const PRIO_COLORS = {
  info: "border-blue-300 bg-blue-50",
  important: "border-amber-400 bg-amber-50",
  urgent: "border-impact-red bg-red-50",
};

const FORFAIT_LABELS = {
  decouverte: "Découverte",
  autonomie_accompagnee: "Autonomie accompagnée",
  vip: "VIP",
  ponctuel: "Ponctuel",
  preparation_competition: "Préparation Compétition",
  autre: "Autre",
};

export default function PrivateLessonsModule() {
  return <PrivateClientsList />;
}

function PrivateClientsList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/private-clients");
      setClients(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="private-lessons-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Suivi clients privés"
          breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Privés / coaching" }]}
          subtitle="Suivi complet : poids · programmes d'entraînement · nutrition · vidéos training · sessions · notifications."
          actions={
            <button onClick={() => setShowForm(true)} data-testid="new-client-btn" className="bg-impact-red text-white px-5 py-2.5 rounded-lg text-sm hover:bg-foreground flex items-center gap-2">
              <Plus size={14}/> Nouveau client privé
            </button>
          }
        />
      </div>

      <div className="px-8 lg:px-12 py-8">
        <MemberPackagesAdmin />
        {loading ? <Loader2 className="animate-spin"/> : clients.length === 0 ? (
          <div className="hard-border p-12 text-center text-muted-foreground">
            <Heart size={48} className="mx-auto mb-3 opacity-30"/>
            Aucun client privé. Clique "Nouveau client privé".
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map(c => (
              <Link to={`/app/cours-prives/${c.id}`} key={c.id} data-testid={`client-card-${c.id}`} className="hard-border p-5 hover:bg-secondary/30 transition-colors">
                <div className="ticker text-impact-gold text-xs">{FORFAIT_LABELS[c.forfait] || "Forfait"}</div>
                <div className="font-display font-bold text-xl mt-1">{c.prenom} {c.nom}</div>
                {c.age_estimation && <div className="text-xs text-muted-foreground">{c.age_estimation}</div>}
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div>Poids dép: <strong>{c.poids_depart_lbs} lb</strong></div>
                  <div>Actuel: <strong>{c.poids_actuel_lbs} lb</strong></div>
                </div>
                {c.poids_depart_lbs && c.poids_actuel_lbs && (
                  <div className={`mt-2 text-sm font-bold ${c.poids_actuel_lbs < c.poids_depart_lbs ? "text-emerald-700" : "text-impact-red"}`}>
                    {(c.poids_actuel_lbs - c.poids_depart_lbs).toFixed(1)} lb
                  </div>
                )}
                {c.prix_total && <div className="text-xs text-muted-foreground mt-2">Forfait : {c.prix_total} $</div>}
              </Link>
            ))}
          </div>
        )}
      </div>

      {showForm && <ClientForm onClose={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ClientForm({ onClose }) {
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", telephone: "", forfait: "decouverte", prix_total: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.prenom || !form.nom) { toast.error("Prénom et nom requis"); return; }
    setSaving(true);
    try {
      await api.post("/private-clients", {
        ...form,
        prix_total: form.prix_total ? parseFloat(form.prix_total) : null,
      });
      toast.success("Client créé");
      onClose();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-bold text-xl mb-4">Nouveau client privé</h2>
        <div className="space-y-2">
          <input placeholder="Prénom *" value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} className="hard-border w-full px-3 py-2" data-testid="form-prenom"/>
          <input placeholder="Nom *" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="hard-border w-full px-3 py-2" data-testid="form-nom"/>
          <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="hard-border w-full px-3 py-2"/>
          <input placeholder="Téléphone" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} className="hard-border w-full px-3 py-2"/>
          <select value={form.forfait} onChange={e => setForm({...form, forfait: e.target.value})} className="hard-border w-full px-3 py-2">
            {Object.entries(FORFAIT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input placeholder="Prix total forfait ($)" type="number" value={form.prix_total} onChange={e => setForm({...form, prix_total: e.target.value})} className="hard-border w-full px-3 py-2"/>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="hard-border px-4 py-2 ticker flex-1">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="form-save" className="bg-impact-red text-white px-4 py-2 ticker flex-1">{saving ? "..." : "Créer"}</button>
        </div>
      </div>
    </div>
  );
}

export function PrivateClientDetail() {
  const { id } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [nutrition, setNutrition] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, p, n, v, s, nf] = await Promise.all([
        api.get(`/private-clients/${id}/dashboard`),
        api.get(`/private-clients/${id}/programs`),
        api.get(`/private-clients/${id}/nutrition`),
        api.get(`/private-clients/${id}/videos`),
        api.get(`/private-clients/${id}/sessions`),
        api.get(`/private-clients/${id}/notifications`),
      ]);
      setDashboard(d.data);
      setPrograms(p.data);
      setNutrition(n.data);
      setVideos(v.data);
      setSessions(s.data);
      setNotifs(nf.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [id]);

  if (loading || !dashboard) {
    return <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto"/></div>;
  }
  const c = dashboard.client;
  const s = dashboard.stats;

  const TABS = [
    { id: "overview", label: "Vue d'ensemble", icon: Heart },
    { id: "weights", label: "Poids", icon: TrendingDown, count: s.weights_count },
    { id: "programs", label: "Programmes", icon: Dumbbell, count: programs.length },
    { id: "nutrition", label: "Nutrition", icon: Apple, count: nutrition.length },
    { id: "videos", label: "Vidéos", icon: VideoIcon, count: videos.length },
    { id: "sessions", label: "Sessions", icon: Calendar, count: sessions.length },
    { id: "notifs", label: "Notifications", icon: Bell, count: notifs.filter(n => !n.sent).length },
  ];

  return (
    <div data-testid="private-client-detail">
      <header className="hard-border-b px-8 lg:px-12 py-6">
        <Link to="/app/cours-prives" className="text-xs ticker flex items-center gap-1 mb-3 hover:text-impact-red">
          <ArrowLeft size={12}/> Retour aux clients privés
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="ticker text-impact-gold text-xs">{FORFAIT_LABELS[c.forfait] || "Forfait"} · {c.prix_total} $</div>
            <h1 className="font-display font-black text-4xl">{c.prenom} {c.nom}</h1>
            {c.age_estimation && <p className="text-sm text-muted-foreground">{c.age_estimation}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="hard-border p-3 bg-secondary/30">
              <div className="ticker text-xs text-muted-foreground">Poids départ</div>
              <div className="font-mono font-bold text-lg">{c.poids_depart_lbs} lb</div>
            </div>
            <div className={`hard-border p-3 ${s.poids_delta_lbs < 0 ? "bg-emerald-50" : "bg-secondary/30"}`}>
              <div className="ticker text-xs text-muted-foreground">Actuel ({s.poids_delta_lbs} lb)</div>
              <div className="font-mono font-bold text-lg">{c.poids_actuel_lbs} lb</div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-wrap border-b">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className={`px-4 py-3 ticker text-xs flex items-center gap-2 border-b-2 ${tab === t.id ? "border-impact-red text-impact-red" : "border-transparent hover:bg-secondary"}`}>
            <t.icon size={14}/> {t.label}
            {t.count !== undefined && <span className="ml-1 opacity-60">({t.count})</span>}
          </button>
        ))}
      </div>

      <div className="px-8 lg:px-12 py-8">
        {tab === "overview" && <OverviewTab c={c} s={s} />}
        {tab === "weights" && <WeightsTab clientId={id} weights={dashboard.weights_timeline} onChange={loadAll}/>}
        {tab === "programs" && <ProgramsTab programs={programs}/>}
        {tab === "nutrition" && <NutritionTab nutrition={nutrition} clientId={id} onChange={loadAll}/>}
        {tab === "videos" && <VideosTab videos={videos} clientId={id} onChange={loadAll}/>}
        {tab === "sessions" && <SessionsTab sessions={sessions} clientId={id} client={c} onChange={loadAll}/>}
        {tab === "notifs" && <NotifsTab notifs={notifs} clientId={id} onChange={loadAll}/>}
      </div>
    </div>
  );
}

function OverviewTab({ c, s }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display font-bold mb-2">Objectifs</h3>
        <ul className="space-y-1 text-sm">
          {(c.objectifs || []).map((o,i) => <li key={i} className="flex items-start gap-2"><span className="text-impact-gold">✦</span> {o}</li>)}
        </ul>
        <h3 className="font-display font-bold mt-4 mb-2">Problèmes santé</h3>
        <ul className="space-y-1 text-sm">
          {(c.problemes_sante || []).map((o,i) => <li key={i} className="text-muted-foreground">· {o}</li>)}
        </ul>
        <h3 className="font-display font-bold mt-4 mb-2">Équipement maison</h3>
        <ul className="space-y-1 text-sm text-muted-foreground">
          {(c.equipement_maison || []).map((o,i) => <li key={i}>· {o}</li>)}
        </ul>
      </div>
      <div>
        <h3 className="font-display font-bold mb-3">Statistiques</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Sessions complétées", s.sessions_completed],
            ["Sessions planifiées", s.sessions_planned],
            ["Programmes", s.programs_count],
            ["Reco nutrition", s.nutrition_count],
            ["Vidéos training", s.videos_count],
            ["Notifs en attente", s.notifications_pending],
            ["Revenue payé", `${s.revenue_paid} $`],
            ["Revenue dû", `${s.revenue_due} $`],
          ].map(([l, v]) => (
            <div key={l} className="hard-border p-3 bg-secondary/30">
              <div className="text-xs text-muted-foreground">{l}</div>
              <div className="font-mono font-bold text-lg">{v}</div>
            </div>
          ))}
        </div>
        {c.notes && (
          <div className="mt-4 p-3 hard-border bg-amber-50">
            <div className="ticker text-xs text-amber-700">— NOTES</div>
            <p className="text-sm">{c.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function WeightsTab({ clientId, weights, onChange }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [poids, setPoids] = useState("");

  const add = async () => {
    if (!poids) return;
    try {
      await api.post(`/private-clients/${clientId}/weights`, { date, poids_lbs: parseFloat(poids), notes: null });
      setPoids("");
      toast.success("Poids ajouté");
      onChange();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="hard-border px-3 py-2"/>
        <input type="number" step="0.1" placeholder="Poids (lb)" value={poids} onChange={e => setPoids(e.target.value)} className="hard-border px-3 py-2 w-32" data-testid="weight-input"/>
        <button onClick={add} data-testid="weight-add" className="bg-impact-red text-white px-4 py-2 ticker">Ajouter</button>
      </div>
      <div className="hard-border">
        <table className="w-full text-sm">
          <thead className="bg-foreground text-white"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2">Poids</th><th className="px-3 py-2">Notes</th></tr></thead>
          <tbody>
            {weights.map(w => (
              <tr key={w.id} className="border-b"><td className="px-3 py-2 font-mono">{w.date}</td><td className="px-3 py-2 text-center font-mono font-bold">{w.poids_lbs} lb</td><td className="px-3 py-2 text-muted-foreground">{w.notes}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProgramsTab({ programs }) {
  if (!programs.length) return <p className="text-muted-foreground">Aucun programme. (Le seed Pierrette inclut déjà 4 programmes.)</p>;
  return (
    <div className="space-y-4">
      {programs.map(p => (
        <div key={p.id} className="hard-border p-5" data-testid={`program-${p.id}`}>
          <div className="ticker text-impact-gold text-xs">{CAT_LABELS[p.categorie] || p.categorie} · {p.lieu}</div>
          <h3 className="font-display font-bold text-xl mt-1 mb-3">{p.titre}</h3>
          <div className="space-y-1 text-sm">
            {(p.exercices || []).map((e, i) => (
              <div key={i} className="flex justify-between border-b py-1">
                <span><strong>{e.nom}</strong> {e.charge && <span className="text-muted-foreground">({e.charge})</span>}</span>
                <span className="text-muted-foreground">{e.reps}</span>
              </div>
            ))}
          </div>
          {p.instructions && <p className="text-xs text-muted-foreground mt-3 italic">{p.instructions}</p>}
        </div>
      ))}
    </div>
  );
}

function NutritionTab({ nutrition, clientId, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {nutrition.map(n => (
        <div key={n.id} className={`hard-border p-4 border-l-4 ${PRIO_COLORS[n.priorite] || ""}`} data-testid={`nutrition-${n.id}`}>
          <div className="ticker text-xs">{n.categorie} · {n.priorite}</div>
          <div className="font-bold mt-1">{n.titre}</div>
          <p className="text-sm text-muted-foreground mt-1">{n.detail}</p>
          <button onClick={async () => { await api.delete(`/private-clients/${clientId}/nutrition/${n.id}`); onChange(); }} className="text-xs text-impact-red mt-2 hover:underline">Supprimer</button>
        </div>
      ))}
    </div>
  );
}

function VideosTab({ videos, clientId, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titre: "", description: "", external_url: "" });

  const addLink = async () => {
    if (!form.titre || !form.external_url) { toast.error("Titre et URL requis"); return; }
    await api.post(`/private-clients/${clientId}/videos`, form);
    setForm({ titre: "", description: "", external_url: "" });
    setShowForm(false);
    onChange();
    toast.success("Vidéo ajoutée");
  };

  return (
    <div>
      <button onClick={() => setShowForm(true)} className="bg-impact-red text-white px-4 py-2 ticker mb-4 flex items-center gap-2"><Plus size={14}/> Ajouter vidéo (lien)</button>
      {showForm && (
        <div className="hard-border p-4 mb-4 bg-secondary/30">
          <input placeholder="Titre" value={form.titre} onChange={e => setForm({...form, titre: e.target.value})} className="hard-border w-full px-3 py-2 mb-2"/>
          <input placeholder="URL YouTube/Vimeo" value={form.external_url} onChange={e => setForm({...form, external_url: e.target.value})} className="hard-border w-full px-3 py-2 mb-2"/>
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="hard-border w-full px-3 py-2 mb-2" rows={2}/>
          <div className="flex gap-2"><button onClick={addLink} className="bg-impact-red text-white px-4 py-2 ticker">Sauver</button><button onClick={() => setShowForm(false)} className="hard-border px-4 py-2 ticker">Annuler</button></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {videos.map(v => (
          <div key={v.id} className="hard-border p-4">
            <div className="font-bold">{v.titre}</div>
            {v.description && <p className="text-xs text-muted-foreground">{v.description}</p>}
            {v.external_url && <a href={v.external_url} target="_blank" rel="noreferrer" className="text-xs text-impact-red flex items-center gap-1 mt-2"><ExternalLink size={10}/> Ouvrir vidéo</a>}
          </div>
        ))}
      </div>
    </div>
  );
}

const TECH_CATS = ["Frappe", "Clinch", "Projection", "Sol / grappling", "Soumission", "Défense", "Cardio / conditionnement", "Mobilité", "Autre"];
const LEVELS = ["", "1 · Découverte", "2 · En apprentissage", "3 · Acquis avec aide", "4 · Autonome", "5 · Maîtrisé"];
const emptyTech = () => ({ name: "", category: "Frappe", level: 2, reps: "", note: "", video_url: "" });

function TechniqueRows({ techs, setTechs }) {
  const up = (i, k, v) => setTechs(techs.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <div className="col-span-2 space-y-2" data-testid="session-techniques">
      <div className="flex items-center justify-between"><span className="ticker text-xs">Techniques pratiquées durant la session</span>
        <button type="button" onClick={() => setTechs([...techs, emptyTech()])} data-testid="tech-add" className="hard-border px-2 py-1 text-xs flex items-center gap-1"><Plus size={12}/> Technique</button></div>
      {techs.map((t, i) => (
        <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`tech-row-${i}`}>
          <input placeholder="Technique (ex. armbar, jab-cross, o-goshi)" value={t.name} onChange={e => up(i, "name", e.target.value)} className="hard-border px-2 py-1.5 text-sm col-span-4" data-testid={`tech-name-${i}`}/>
          <select value={t.category} onChange={e => up(i, "category", e.target.value)} className="hard-border px-2 py-1.5 text-sm col-span-2">{TECH_CATS.map(c => <option key={c}>{c}</option>)}</select>
          <select value={t.level} onChange={e => up(i, "level", parseInt(e.target.value))} className="hard-border px-2 py-1.5 text-sm col-span-2" data-testid={`tech-level-${i}`}>{LEVELS.map((l, v) => v ? <option key={v} value={v}>{l}</option> : null)}</select>
          <input placeholder="Reps/rounds" value={t.reps} onChange={e => up(i, "reps", e.target.value)} className="hard-border px-2 py-1.5 text-sm col-span-1"/>
          <input placeholder="Note factuelle" value={t.note} onChange={e => up(i, "note", e.target.value)} className="hard-border px-2 py-1.5 text-sm col-span-2"/>
          <button type="button" onClick={() => setTechs(techs.filter((_, j) => j !== i))} className="text-xs text-impact-red col-span-1">✕</button>
          <input placeholder="🎬 Lien vidéo démo (YouTube / Vimeo / Bunny) — le client la revoit à la maison" value={t.video_url || ""} onChange={e => up(i, "video_url", e.target.value)} className="hard-border px-2 py-1.5 text-xs col-span-11" data-testid={`tech-video-${i}`}/>
        </div>))}
    </div>);
}

function SessionsTab({ sessions, clientId, client, onChange }) {
  const [reminderOn, setReminderOn] = useState(client?.review_reminder_enabled !== false);
  const toggleReminder = async (v) => { setReminderOn(v); try { await api.patch(`/private-clients/${clientId}/review-reminder`, { enabled: v }); toast.success(v ? "Rappel SMS hebdo activé (dimanche 18h)" : "Rappel SMS hebdo désactivé"); } catch { toast.error("Erreur"); setReminderOn(!v); } };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date_heure: "", duree_minutes: 60, lieu: "dojo", focus: "", tarif: "", notes: "" });
  const [techs, setTechs] = useState([emptyTech()]);
  const [editing, setEditing] = useState(null);
  const [editTechs, setEditTechs] = useState([]);
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get(`/private-clients/${clientId}/techniques`).then(r => setSummary(r.data)).catch(() => {}); }, [clientId, sessions]);

  const add = async () => {
    if (!form.date_heure) { toast.error("Date requise"); return; }
    await api.post(`/private-clients/${clientId}/sessions`, { ...form, tarif: form.tarif ? parseFloat(form.tarif) : null, techniques: techs.filter(t => t.name.trim()) });
    setShowForm(false); setForm({ date_heure: "", duree_minutes: 60, lieu: "dojo", focus: "", tarif: "", notes: "" }); setTechs([emptyTech()]);
    onChange(); toast.success("Session créée");
  };
  const saveTechs = async (sid) => {
    await api.patch(`/private-clients/${clientId}/sessions/${sid}`, { techniques: editTechs.filter(t => t.name.trim()) });
    setEditing(null); onChange(); toast.success("Techniques enregistrées");
  };

  return (
    <div>
      {summary && summary.techniques.length > 0 && (
        <div className="hard-border p-3 mb-4 bg-secondary/20" data-testid="tech-summary">
          <div className="ticker text-xs mb-2">Suivi des techniques · {summary.techniques.length} technique(s) sur {summary.sessions} session(s)</div>
          <div className="flex flex-wrap gap-1.5">{summary.techniques.map(t => (
            <span key={t.name} title={`${t.category} · ${t.count} session(s) · niveau ${t.first_level || "?"} → ${t.last_level || "?"}`} className="hard-border px-2 py-1 text-xs flex items-center gap-1.5" data-testid={`tech-chip-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <b>{t.name}</b><span className="opacity-60">×{t.count}</span>{t.last_level && <span className="px-1 bg-foreground text-white text-[10px]">niv. {t.last_level}</span>}{t.progress > 0 && <span className="text-emerald-600 text-[10px]">▲ +{t.progress}</span>}</span>))}</div>
        </div>)}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setShowForm(true)} data-testid="session-new" className="bg-impact-red text-white px-4 py-2 ticker flex items-center gap-2"><Plus size={14}/> Nouvelle session</button>
        <button onClick={async () => { try { const r = await api.post(`/private-clients/${clientId}/review-reminder`); toast.success(`📱 SMS de révision envoyé (${r.data.sent})`); } catch (e) { toast.error(e.response?.data?.detail || "SMS impossible"); } }} data-testid="review-reminder-now-btn" className="hard-border px-4 py-2 ticker flex items-center gap-2" title="Envoyer maintenant le SMS hebdo avec le lien des vidéos à réviser">📱 SMS révision maintenant</button>
        <label className="hard-border px-3 py-2 ticker flex items-center gap-2 text-xs cursor-pointer" title="SMS automatique chaque dimanche avec le lien des vidéos à réviser (si téléphone + au moins une vidéo)">
          <input type="checkbox" checked={reminderOn} onChange={(e) => toggleReminder(e.target.checked)} data-testid="review-reminder-toggle" className="accent-[#D90429]" /> Rappel hebdo auto
        </label>
        <button onClick={async () => { try { const r = await api.get(`/private-clients/${clientId}/monthly-recap`); if (!r.data.recap) { toast.info("Aucune séance le mois dernier — rien à envoyer."); return; } const w = window.open("", "_blank", "noopener"); if (w) { w.document.write(`<title>Bilan ${r.data.month}</title>${r.data.html}`); w.document.close(); } if (window.confirm(`Envoyer ce bilan de ${r.data.month} à ${r.data.client.email || "(pas de courriel)"} ?`)) { const s2 = await api.post(`/private-clients/${clientId}/monthly-recap/send`); toast.success(`📓 Bilan envoyé à ${s2.data.to}`); } } catch (e) { toast.error(e.response?.data?.detail || "Bilan impossible"); } }} data-testid="monthly-recap-btn" className="hard-border px-4 py-2 ticker flex items-center gap-2" title="Aperçu + envoi du bilan mensuel (mois précédent) — envoi automatique le 1er du mois">📓 Bilan mensuel</button>
        <button onClick={async () => { try { const r = await api.post(`/private-clients/${clientId}/review-link`); const url = `${window.location.origin}${r.data.path}`; try { await navigator.clipboard.writeText(url); } catch (_) {} window.open(url, "_blank", "noopener"); toast.success("Lien de révision copié — envoie-le au client (techniques + vidéos démo)."); } catch { toast.error("Impossible de créer le lien."); } }} data-testid="review-link-btn" className="hard-border px-4 py-2 ticker flex items-center gap-2">🎬 Lien révision client (vidéos)</button>
      </div>
      {showForm && (
        <div className="hard-border p-4 mb-4 bg-secondary/30 grid grid-cols-2 gap-2">
          <input type="datetime-local" value={form.date_heure} onChange={e => setForm({...form, date_heure: e.target.value})} className="hard-border px-3 py-2" data-testid="session-date"/>
          <input type="number" placeholder="Durée (min)" value={form.duree_minutes} onChange={e => setForm({...form, duree_minutes: parseInt(e.target.value)})} className="hard-border px-3 py-2"/>
          <select value={form.lieu} onChange={e => setForm({...form, lieu: e.target.value})} className="hard-border px-3 py-2">
            <option value="dojo">Dojo</option><option value="domicile">Domicile</option><option value="salle">Salle</option><option value="virtuel">Virtuel</option>
          </select>
          <input type="number" step="0.01" placeholder="Tarif ($)" value={form.tarif} onChange={e => setForm({...form, tarif: e.target.value})} className="hard-border px-3 py-2"/>
          <input placeholder="Focus de la session" value={form.focus} onChange={e => setForm({...form, focus: e.target.value})} className="hard-border px-3 py-2 col-span-2"/>
          <TechniqueRows techs={techs} setTechs={setTechs}/>
          <textarea placeholder="Notes de session (observations factuelles, devoirs, prochaine étape)" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="hard-border px-3 py-2 col-span-2"/>
          <div className="col-span-2 flex gap-2"><button onClick={add} data-testid="session-save" className="bg-impact-red text-white px-4 py-2 ticker">Sauver</button><button onClick={() => setShowForm(false)} className="hard-border px-4 py-2 ticker">Annuler</button></div>
        </div>
      )}
      <div className="hard-border">
        <table className="w-full text-sm">
          <thead className="bg-foreground text-white"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2">Durée</th><th className="px-3 py-2">Lieu</th><th className="px-3 py-2 text-left">Focus · techniques</th><th className="px-3 py-2">Tarif</th><th className="px-3 py-2">Statut</th></tr></thead>
          <tbody>
            {sessions.map(s => (
              <Fragment key={s.id}>
              <tr className="border-b" data-testid={`session-row-${s.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{new Date(s.date_heure).toLocaleString("fr-CA")}</td>
                <td className="px-3 py-2 text-center">{s.duree_minutes} min</td>
                <td className="px-3 py-2 text-center text-xs">{s.lieu}</td>
                <td className="px-3 py-2 text-xs">{s.focus}
                  <div className="flex flex-wrap gap-1 mt-1">{(s.techniques || []).map((t, i) => <span key={i} className="px-1.5 py-0.5 bg-secondary text-[11px]" title={`${t.category || ""} ${t.reps ? "· " + t.reps : ""} ${t.note ? "· " + t.note : ""}`}>{t.name}{t.level ? ` · niv. ${t.level}` : ""}{t.video_url && <a href={t.video_url} target="_blank" rel="noreferrer" className="ml-1" title="Vidéo démo" data-testid={`tech-video-link-${s.id}-${i}`}>🎬</a>}</span>)}
                    <button onClick={() => { setEditing(s.id); setEditTechs((s.techniques || []).length ? s.techniques : [emptyTech()]); }} data-testid={`session-edit-tech-${s.id}`} className="text-[11px] text-impact-red underline">{(s.techniques || []).length ? "modifier" : "+ techniques"}</button></div></td>
                <td className="px-3 py-2 text-right font-mono">{s.tarif} $</td>
                <td className="px-3 py-2 text-center text-xs">{s.status}</td>
              </tr>
              {editing === s.id && <tr className="border-b bg-secondary/20"><td colSpan={6} className="px-3 py-3"><div className="grid grid-cols-2 gap-2"><TechniqueRows techs={editTechs} setTechs={setEditTechs}/>
                <div className="col-span-2 flex gap-2"><button onClick={() => saveTechs(s.id)} data-testid="session-tech-save" className="bg-impact-red text-white px-4 py-1.5 ticker text-xs">Enregistrer les techniques</button><button onClick={() => setEditing(null)} className="hard-border px-4 py-1.5 ticker text-xs">Annuler</button></div></div></td></tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NotifsTab({ notifs, clientId, onChange }) {
  const [form, setForm] = useState({ titre: "", message: "", scheduled_for: "" });
  const [showForm, setShowForm] = useState(false);

  const add = async () => {
    if (!form.titre || !form.scheduled_for) { toast.error("Titre et date requis"); return; }
    await api.post(`/private-clients/${clientId}/notifications`, form);
    setShowForm(false);
    setForm({ titre: "", message: "", scheduled_for: "" });
    onChange();
    toast.success("Notification créée");
  };

  return (
    <div>
      <button onClick={() => setShowForm(true)} className="bg-impact-red text-white px-4 py-2 ticker mb-4 flex items-center gap-2"><Plus size={14}/> Nouvelle notification</button>
      {showForm && (
        <div className="hard-border p-4 mb-4 bg-secondary/30 space-y-2">
          <input placeholder="Titre" value={form.titre} onChange={e => setForm({...form, titre: e.target.value})} className="hard-border w-full px-3 py-2"/>
          <input type="datetime-local" value={form.scheduled_for} onChange={e => setForm({...form, scheduled_for: e.target.value})} className="hard-border w-full px-3 py-2"/>
          <textarea placeholder="Message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="hard-border w-full px-3 py-2" rows={3}/>
          <div className="flex gap-2"><button onClick={add} className="bg-impact-red text-white px-4 py-2 ticker">Sauver</button><button onClick={() => setShowForm(false)} className="hard-border px-4 py-2 ticker">Annuler</button></div>
        </div>
      )}
      <div className="space-y-2">
        {notifs.map(n => (
          <div key={n.id} className={`hard-border p-3 ${n.sent ? "opacity-50" : ""}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-bold">{n.titre}</div>
                <div className="text-xs text-muted-foreground">{new Date(n.scheduled_for).toLocaleString("fr-CA")}</div>
                {n.message && <p className="text-sm mt-1">{n.message}</p>}
              </div>
              <div className="flex gap-2">
                {!n.sent && <button onClick={async () => { await api.patch(`/private-clients/${clientId}/notifications/${n.id}/mark-sent`); onChange(); }} className="text-xs ticker bg-emerald-600 text-white px-2 py-1">Marquer envoyé</button>}
                <button onClick={async () => { await api.delete(`/private-clients/${clientId}/notifications/${n.id}`); onChange(); }} className="text-impact-red"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
